import React, { useMemo } from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Download, Filter, Briefcase } from 'lucide-react';
import type { Timesheet, Employee, Project } from '@/types';
import type { ProjectUtilisation } from '@/types/utilisation';
import { calculateProjectDetailData, exportProjectReportCSV } from '@/utils/projectDetailUtils';
import { toast } from 'sonner';

interface ProjectDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: ProjectUtilisation;
  timesheets: Timesheet[];
  employees: Employee[];
  projects: Project[];
  dateRange: { start: Date; end: Date };
  onFilterToProject: (projectId: string) => void;
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export const ProjectDetailModal: React.FC<ProjectDetailModalProps> = ({
  isOpen,
  onClose,
  project,
  timesheets,
  employees,
  projects,
  dateRange,
  onFilterToProject,
}) => {
  const detailData = useMemo(
    () => calculateProjectDetailData(project, timesheets, employees, projects, dateRange),
    [project, timesheets, employees, projects, dateRange]
  );

  const handleExport = () => {
    try {
      const csv = exportProjectReportCSV(detailData);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `project-${project.projectName.replace(/\s+/g, '-')}-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Project report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const handleFilterToProject = () => {
    onFilterToProject(project.projectId);
    onClose();
    toast.success(`Filtered to ${project.projectName}`);
  };

  const weeklyChartData = detailData.weeklyTrend.map((week) => ({
    week: week.week,
    'Total Hours': week.totalHours,
    'Billable Hours': week.billableHours,
  }));

  const employeePieData = detailData.employeeAllocation.slice(0, 6).map((emp, index) => ({
    name: emp.employeeName,
    value: emp.hours,
    percentage: emp.percentage,
    fill: COLORS[index % COLORS.length],
  }));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            {project.projectName}
          </DialogTitle>
          <DialogDescription>
            {project.clientName} • {format(dateRange.start, 'MMM d')} -{' '}
            {format(dateRange.end, 'MMM d, yyyy')}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1">
          <ScrollAreaViewport className="max-h-[calc(90vh-180px)] pr-4">
            <div className="space-y-6 pb-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{project.totalHours.toFixed(1)}</div>
                  <p className="text-xs text-muted-foreground">Total Hours</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {project.billableHours.toFixed(1)}
                  </div>
                  <p className="text-xs text-muted-foreground">Billable Hours</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">
                    {(project.utilisationRate * 100).toFixed(1)}%
                  </div>
                  <p className="text-xs text-muted-foreground">Utilisation Rate</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{project.employeeCount}</div>
                  <p className="text-xs text-muted-foreground">Employees</p>
                </CardContent>
              </Card>
            </div>

            {/* Weekly Trend Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Weekly Trend</CardTitle>
                <CardDescription>Hours logged per week</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={weeklyChartData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="week" className="text-xs" tick={{ fill: 'currentColor' }} />
                    <YAxis className="text-xs" tick={{ fill: 'currentColor' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--background))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '6px',
                      }}
                    />
                    <Legend />
                    <Bar dataKey="Total Hours" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Billable Hours" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Employee Allocation */}
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Employee Allocation</CardTitle>
                  <CardDescription>Time distribution by employee</CardDescription>
                </CardHeader>
                <CardContent>
                  {employeePieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={employeePieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(props) => `${(Number(props.percent ?? 0) * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {employeePieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--background))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '6px',
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                      No employee data available
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Employee Details</CardTitle>
                  <CardDescription>Hours per employee</CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[200px]">
                    <div className="space-y-2">
                      {detailData.employeeAllocation.map((emp, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: COLORS[index % COLORS.length] }}
                            />
                            <span className="truncate max-w-[150px]">{emp.employeeName}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{emp.hours.toFixed(1)}h</span>
                            <Badge variant="secondary" className="text-xs">
                              {emp.percentage.toFixed(0)}%
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
            </div>
          </ScrollAreaViewport>
          <ScrollAreaScrollbar orientation="vertical" />
        </ScrollArea>

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button variant="outline" onClick={handleFilterToProject} className="gap-2">
            <Filter className="h-4 w-4" />
            Filter to This Project
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExport} className="gap-2">
              <Download className="h-4 w-4" />
              Export Report
            </Button>
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
